import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { useFetch, PageHeader, Loading, ConfirmDialog } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function Users() {
  const { data: users, loading, refetch } = useFetch("/users", []);
  const { data: roles } = useFetch("/roles", []);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [del, setDel] = useState(null);

  const save = async () => {
    try {
      if (f.id) await client.put(`/users/${f.id}`, f);
      else await client.post("/users", f);
      toast.success("Saved"); setOpen(false); refetch();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    try { await client.delete(`/users/${del.id}`); toast.success("Deleted"); setDel(null); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage staff accounts and permissions" testid="users-page">
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setF({ role: "coach" }); setOpen(true); }} data-testid="user-add"><Plus size={16} className="me-1" /> Add User</Button>
      </PageHeader>
      {loading ? <Loading /> : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border"><tr>
              {["Name", "Email", "Role", ""].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {(users || []).map((u) => (
                <tr key={u.id} className="hover:bg-muted/40" data-testid={`user-${u.id}`}>
                  <td className="px-4 py-3 font-medium flex items-center gap-2"><div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">{u.name?.[0]?.toUpperCase()}</div>{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary">{u.role_label}</span></td>
                  <td className="px-4 py-3 text-end">
                    <Button size="icon" variant="ghost" onClick={() => { setF({ ...u }); setOpen(true); }} data-testid={`user-edit-${u.id}`}><Pencil size={15} /></Button>
                    {u.role !== "super_admin" && <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setDel(u)} data-testid={`user-delete-${u.id}`}><Trash2 size={15} /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{f.id ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="mb-1.5 block">Name</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
            {!f.id && <div><Label className="mb-1.5 block">Email</Label><Input type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>}
            <div><Label className="mb-1.5 block">Password {f.id && "(leave blank to keep)"}</Label><Input type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" /></div>
            <div><Label className="mb-1.5 block">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{(roles || []).map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="user-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Delete user?" description={`Permanently remove ${del?.name}?`} onConfirm={doDelete} />
    </div>
  );
}
